import { useEffect, useRef, useState } from 'react'
import { useCountdown } from '@renderer/utils/countdownUtils'

/**
 * Page timeouts, read from the project config.
 *
 * Every BK page waits on the guest, and every one has a timeout setting in the
 * project (checkoutTimeout, filterTimeout, …). When it runs out the page does
 * what the stock page does: carry on with what is chosen.
 *
 * One deliberate difference from stock: a value of null, 0 or absent means the
 * page has NO timeout. Several stock pages substitute a default instead
 * (`captureTimeout ?? 300`, `retakeTimeout || 60`, `resultTimeout || 120`),
 * which makes "turned off" impossible to express from the dashboard.
 */
export const timeoutSeconds = (configured: number | null | undefined): number | null =>
  typeof configured === 'number' && configured > 0 ? configured : null

/** The slice of `useCountdown`'s return value a page timeout needs. */
export interface TimeoutClock {
  setOnTick: (callback: ((seconds: number) => void) | null) => void
  setOnComplete: (callback: (() => void) | null) => void
  setRemainingSeconds: (seconds: number) => void
  start: () => void
  stop: () => void
}

/**
 * Wire and start one page timeout. Returns the teardown.
 *
 * Callbacks go on before the clock starts, and the teardown both stops it and
 * unhooks them — a stale onExpire firing after the page has moved on would
 * navigate a guest who is already somewhere else.
 */
export function armPageTimeout(
  clock: TimeoutClock,
  seconds: number,
  onTick: (remaining: number) => void,
  onExpire: () => void
): () => void {
  clock.setOnTick(onTick)
  clock.setOnComplete(onExpire)
  clock.setRemainingSeconds(seconds)
  onTick(seconds)
  clock.start()
  return (): void => {
    clock.stop()
    clock.setOnComplete(null)
    clock.setOnTick(null)
  }
}

/**
 * Run a page's timeout while `active`, and report the seconds left for display
 * (null when there is no timeout running).
 *
 * The countdown and the expiry handler are reached through refs: `useCountdown`
 * returns a new object on every render, and an effect that depended on it
 * would restart the clock on every render — the same mistake that froze the
 * capture page. Only the configured seconds and `active` restart it.
 */
export function useBkPageTimeout(
  configured: number | null | undefined,
  onExpire: () => void,
  active = true
): number | null {
  const seconds = timeoutSeconds(configured)
  const countdown = useCountdown(seconds ?? 1)
  const clockRef = useRef(countdown)
  clockRef.current = countdown
  const onExpireRef = useRef(onExpire)
  onExpireRef.current = onExpire

  const [remaining, setRemaining] = useState<number | null>(null)

  useEffect(() => {
    if (seconds === null || !active) {
      setRemaining(null)
      return
    }
    return armPageTimeout(clockRef.current, seconds, setRemaining, () => {
      setRemaining(0)
      onExpireRef.current()
    })
  }, [seconds, active])

  return remaining
}
